import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAppointmentStatusColor,
  getAppointmentStatusLabel,
  isAppointmentInProgress,
  useAppointmentDetail,
  useAppointmentsByWorkOrder,
} from "../../entities/appointment";
import { useAuthStore } from "../../features/auth";
import { Button, FieldWrapper, Input, Loader, MobilePageLayout, PageLayout } from "../../shared/ui";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { SuccessDialog } from "../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { PATHS } from "../../app/routes/paths";
import { AttachmentsSection } from "../../features/disposition/ui/AttachmentsSection";
import { WorkerAppointmentPlanOverlay } from "../../widgets/worker-appointment-plan/ui/WorkerAppointmentPlanOverlay";
import { WorkerAppointmentReschedulePlanOverlay } from "../../widgets/worker-appointment-plan/ui/WorkerAppointmentReschedulePlanOverlay";
import {
  buildObjektString,
  buildTitle,
  formatDateTime,
  getSchadensbeschreibung,
} from "./lib";
import { AppointmentActions } from "./ui/AppointmentActions";
import { AppointmentDurationPills } from "./ui/AppointmentDurationPills";
import { AppointmentHeader } from "./ui/AppointmentHeader";
import { CompleteAppointmentModal } from "./ui/CompleteAppointmentModal";
import { FollowUpAppointmentModal } from "./ui/FollowUpAppointmentModal";
import { PreviousAppointmentsList } from "./ui/PreviousAppointmentsList";
import { StartAppointmentModal } from "./ui/StartAppointmentModal";

export const WorkerAppointmentPage = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isMobile = useIsMobile();
  const { data, isLoading, isError } = useAppointmentDetail(appointmentId);
  const isFollowUp = data && data.sequenceNumber > 1;
  const { data: siblingAppointments } = useAppointmentsByWorkOrder(
    isFollowUp ? data.workOrderId : null,
  );
  const previousAppointments = (siblingAppointments ?? []).filter((a) => a.id !== appointmentId);

  const [showStartModal, setShowStartModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successIsFollowUp, setSuccessIsFollowUp] = useState(false);
  const [showPlanOverlay, setShowPlanOverlay] = useState(false);
  const [showPlanSuccess, setShowPlanSuccess] = useState(false);
  const [showReschedulePlanOverlay, setShowReschedulePlanOverlay] = useState(false);
  const [showRescheduleSuccess, setShowRescheduleSuccess] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center h-full text-red-400">
        Termin konnte nicht geladen werden.
      </div>
    );
  }

  const ext = data.workOrder?.external;
  const showActionButtons = data.status !== 'COMPLETED' && data.status !== 'COMPLETED_WITH_FOLLOW_UP';
  const isInProgress = isAppointmentInProgress(data);

  const fields = (
    <>
      <FieldWrapper label="Behebungstermin">
        <Input value={formatDateTime(data.plannedStart)} disabled />
      </FieldWrapper>

      <AppointmentDurationPills
        variant="planned"
        work={data.plannedWorkDuration}
        travel={data.plannedTravelDuration}
        departure={data.plannedDepartureDuration}
      />
      {(data.status === 'COMPLETED' || data.status === 'COMPLETED_WITH_FOLLOW_UP') && data.actualWorkDuration != null && data.actualTravelDuration != null && (
        <AppointmentDurationPills
          variant="actual"
          work={data.actualWorkDuration}
          travel={data.actualTravelDuration}
          departure={data.actualDepartureDuration}
        />
      )}
      <FieldWrapper
        label="Objekt"
        topChildren={
          ext?.gebaeude ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ext.gebaeude)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto flex items-center gap-1 text-[11px] text-blue-600 font-medium px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              Maps
            </a>
          ) : undefined
        }
      >
        <Input value={buildObjektString(ext)} disabled />
      </FieldWrapper>

      <FieldWrapper label="Mieter">
        {(() => {
          const m = ext?.mieter;
          const hasAny = m?.name || m?.telefon || m?.email;
          if (!hasAny) {
            return (
              <div className="rounded border border-slate-200 bg-slate-100 px-3 py-2 min-h-[100px] text-sm text-slate-500">
                —
              </div>
            );
          }
          return (
            <div className="rounded border border-slate-200 bg-slate-100 px-3 py-2 min-h-[100px] text-sm flex flex-col gap-1">
              {m?.name && <span>{m.name}</span>}
              {m?.telefon && (
                <a
                  href={`tel:${m.telefon.replace(/\s+/g, '')}`}
                  className="text-blue-600 underline w-fit"
                >
                  {m.telefon}
                </a>
              )}
              {m?.email && (
                <a
                  href={`mailto:${m.email}`}
                  className="text-blue-600 underline w-fit"
                >
                  {m.email}
                </a>
              )}
            </div>
          );
        })()}
      </FieldWrapper>

      <FieldWrapper label="Gewerk">
        <Input value={ext?.gewerkeText ?? "—"} disabled />
      </FieldWrapper>

      <FieldWrapper label="Schadensbeschreibung">
        <Input value={getSchadensbeschreibung(ext)} disabled as="textarea" inputClassName="!min-h-[100px]" />
      </FieldWrapper>

      {data.description && (
        <FieldWrapper label="Beschreibung">
          <Input value={data.description} disabled as="textarea" inputClassName="!min-h-[100px]" />
        </FieldWrapper>
      )}

      {previousAppointments.length > 0 && (
        <PreviousAppointmentsList appointments={previousAppointments} />
      )}

      {data.attachments && data.attachments.length > 0 && (
        <AttachmentsSection
          existingAttachments={data.attachments}
          onRemoveExistingAttachment={() => { }}
          files={[]}
          onFilesChange={() => { }}
          readonly
        />
      )}
    </>
  );

  const modals = (
    <>
      {appointmentId && (
        <StartAppointmentModal
          show={showStartModal}
          setShow={setShowStartModal}
          appointmentId={appointmentId}
          workOrderId={data.workOrderId}
          plannedTravelDuration={data.plannedTravelDuration}
          onSuccess={() => setShowStartModal(false)}
        />
      )}

      {appointmentId && (
        <CompleteAppointmentModal
          show={showCompleteModal}
          setShow={setShowCompleteModal}
          appointmentId={appointmentId}
          plannedWorkDuration={data.plannedWorkDuration}
          plannedDepartureDuration={data.plannedDepartureDuration}
          actualStart={data.actualStart}
          onSuccess={() => {
            setShowCompleteModal(false);
            setSuccessIsFollowUp(false);
            setShowSuccess(true);
          }}
        />
      )}

      {appointmentId && (
        <FollowUpAppointmentModal
          show={showFollowUpModal}
          setShow={setShowFollowUpModal}
          appointmentId={appointmentId}
          plannedWorkDuration={data.plannedWorkDuration}
          plannedDepartureDuration={data.plannedDepartureDuration}
          actualStart={data.actualStart}
          onSuccess={() => {
            setShowFollowUpModal(false);
            setSuccessIsFollowUp(true);
            setShowSuccess(true);
          }}
        />
      )}

      <SuccessDialog
        open={showSuccess}
        onOpenChange={setShowSuccess}
        title="Zeiterfassung wurde erfolgreich gespeichert"
        onClose={() => {
          setShowSuccess(false);
          setSuccessIsFollowUp(false);
        }}
        secondaryAction={successIsFollowUp && data.workOrderId ? {
          label: "Folgetermin planen",
          onClick: () => {
            setShowSuccess(false);
            setSuccessIsFollowUp(false);
            setShowPlanOverlay(true);
          },
        } : undefined}
      />

      <WorkerAppointmentPlanOverlay
        show={showPlanOverlay}
        onClose={() => setShowPlanOverlay(false)}
        onSuccess={() => {
          setShowPlanOverlay(false);
          setShowPlanSuccess(true);
        }}
        workOrderId={data.workOrderId}
        tradeCode={data.tradeCode}
        technicianId={Number(user?.id ?? 0)}
      />

      <SuccessDialog
        open={showPlanSuccess}
        onOpenChange={setShowPlanSuccess}
        title="Folgetermin erfolgreich geplant"
        onClose={() => {
          setShowPlanSuccess(false);
          navigate(PATHS.workerCalendar);
        }}
      />

      <WorkerAppointmentReschedulePlanOverlay
        show={showReschedulePlanOverlay}
        onClose={() => setShowReschedulePlanOverlay(false)}
        onSuccess={() => {
          setShowReschedulePlanOverlay(false);
          setShowRescheduleSuccess(true);
        }}
        appointment={data}
        technicianId={Number(user?.id ?? 0)}
      />

      <SuccessDialog
        open={showRescheduleSuccess}
        onOpenChange={setShowRescheduleSuccess}
        title="Termin erfolgreich umgeplant"
        onClose={() => {
          setShowRescheduleSuccess(false);
          navigate(PATHS.workerCalendar);
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobilePageLayout
          header={
            <div className="px-4 pt-4">
              <button onClick={() => navigate(-1)} className="text-blue-600 text-sm font-medium">
                ← Zurück
              </button>
            </div>
          }
          actions={showActionButtons ? (
            <AppointmentActions
              isInProgress={isInProgress}
              onStart={() => setShowStartModal(true)}
              onMarkDone={() => setShowCompleteModal(true)}
              onFollowUp={() => setShowFollowUpModal(true)}
              onReschedule={() => setShowReschedulePlanOverlay(true)}
            />
          ) : undefined}
        >
          <div className="px-4 pb-4 space-y-4 pt-3">
            <AppointmentHeader
              title={buildTitle(ext, data.workOrderId)}
              statusLabel={getAppointmentStatusLabel(data.status, data)}
              status={data.status}
            />
            {fields}
          </div>
        </MobilePageLayout>
        {modals}
      </>
    );
  }

  return (
    <>
      <PageLayout
        pageTitle={`Reparaturauftrag ${buildTitle(ext, data.workOrderId)}`}
        back
        headerChildren={showActionButtons ? (
          <>
            {!isInProgress && (
              <Button
                variant="surface"
                onClick={() => setShowReschedulePlanOverlay(true)}
              >
                Umplanen
              </Button>
            )}
            {isInProgress &&
              <Button
                variant="surface"
                onClick={() => setShowFollowUpModal(true)}
              >
                Folgetermin
              </Button>
            }
            {isInProgress ? (
              <Button
                className="!bg-green-500 !text-white"
                onClick={() => setShowCompleteModal(true)}
              >
                Arbeit beenden
              </Button>
            ) : (
              <Button
                className="!bg-green-500 !text-white"
                onClick={() => setShowStartModal(true)}
              >
                Arbeit starten
              </Button>
            )}
          </>
        ) : undefined}
      >
        <div className="space-y-4">
          <p className="text-sm font-medium" style={{ color: getAppointmentStatusColor(data.status, data) }}>
            {getAppointmentStatusLabel(data.status, data)}
          </p>
          {fields}
        </div>
      </PageLayout>
      {modals}
    </>
  );
};
